import graphql from 'lib/api/graphql';
import updateConversationAttributes from 'graphQL/mutations/updateConversationAttributes.graphql';
import { useCallback, useEffect, useMemo } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import { useMessages } from 'features/messaging';
import { Conversation as TwilioConversation } from '@twilio/conversations';
import ensureParticipants from 'features/messaging/graphql/mutations/ensure-conversation-participants.graphql';

/**
 * @typedef {Object} UseManageTwilioConversationsHookProps
 * @property {string} impersonatedUserId - Optional. The user's ID to dependency-inject for administrative esthetician impersonation.
 */

/**
 * @typedef {Object} UseManageTwilioConversationsHookValue
 * @property {Function} handleConversationJoined
 * @property {Function} handleConversationLeft
 * @property {Function} handleConversationUpdated
 * @property {number} unreadConversationsCount
 * @property {{ [conversationSID: string]: import('@twilio/conversations').Conversation }} - Twilio Conversation instances
 * @property {{ [conversationSID: string]: Conversation }} conversations - Raw POJO conversations passed to children for state updates
 */

/**
 * @description
 * - Manages Conversations state,
 * - Manages unread conversations count
 * @param {UseManageTwilioConversationsProps} props
 * @returns {UseManageTwilioConversationsHookValue}
 */
export const useManageTwilioConversations = () => {
  // Conversations Loaded from the server
  const {
    conversationsByConversationSid: conversationsFromServer,
    actions: { markConversationRead: markConversationReadOnServer },
  } = useMessages(true);

  /**
   * @type {[
   *  import('@twilio/conversations/lib/conversation').Conversation[],
   *  import('react').Dispatch<
   *    import('react').SetStateAction<
   *      { [conversationSid: string]: import('@twilio/conversations/lib/conversation').Conversation[]
   *    >
   *  >
   * ]} - A map of Twilio Conversation instances
   */
  const [conversationsByConversationSID, _setConversationsByConversationSID] =
    useStateIfMounted(conversationsFromServer ?? {});

  const [conversationsCountLoading, _setConversationsCountLoading] =
    useStateIfMounted(false);

  /**
   * @type {[
   *  { [conversationSid: string]: number },
   *  import('react').Dispatch<
   *    import('react').SetStateAction<
   *      { [conversationSid: string]: number }
   *    > }
   *  >
   * ]}
   */
  const [
    unreadMessagesCountByConversationSID,
    _setUnreadMessagesCountByConversationSID,
  ] = useStateIfMounted({});

  const [unreadConversationsCount, _setUnreadConversationsCount] =
    useStateIfMounted(0);

  /**
   * Conversations are instances of the @twilio/conversations Conversation class,
   * whose internal values are accessible via getter (the values are non-
   * serializable).
   *
   * Call the getters whose values we need to make them accessible on POJOs that
   * can be stored in state and reliably trigger necessary re-renders.
   */
  const rawConversations = useMemo(() => {
    /**
     * @param {{ [conversationSid: string]: Conversation }} accumulator
     * @param {(import('@twilio/conversations/lib/conversation').Conversation)} conversation
     * @returns {{ [conversationSid: string]: Conversation }}
     */
    const reducer = (accumulator, conversation) => {
      return {
        ...accumulator,

        [conversation.sid]: {
          ...conversation,

          /**
           * While it looks like these properties would be covered by the above ...conversation,
           * they are not because they exist on the Twilio Conversation.prototype as getters and
           * must be explicity called to be made accessible.
           */
          attributes: conversation.attributes,
          dateCreated: conversation.dateCreated,
          friendlyName: conversation.friendlyName,
          uniqueName: conversation.uniqueName,

          /** Flatten nested data structure so that React can see state updates */
          ...(conversation.source === 'sdk' && {
            dateUpdated:
              conversation?.lastMessage?.dateCreated ??
              conversation.dateUpdated,
            lastMessageCreated: conversation?.lastMessage?.dateCreated,
            lastMessageIndex: conversation?.lastMessage?.index,
          }),
        },
      };
    };

    return Object.values(conversationsByConversationSID).reduce(reducer, {});
  }, [conversationsByConversationSID]);

  /**
   * @param {import('@twilio/conversations/lib/conversation').Conversation } conversation
   * @returns {Promise<void>}
   */
  const _handleConversationJoined = async conversation => {
    // No need to add initialized conversation to the state if it is already in there
    const existing =
      conversationsByConversationSID[conversation.sid].source == 'sdk';

    if (existing) return;

    if (conversation instanceof TwilioConversation) {
      const participants = await conversation.getParticipants();
      const mobileParticipant = participants.find(({ type }) => type === 'sms');
      _setConversationsByConversationSID(previousState => {
        const newConversations = {
          ...previousState,
          [conversation.sid]: conversation,
        };
        newConversations[conversation.sid].source = 'sdk';
        newConversations[conversation.sid].hasMobileParticipants =
          mobileParticipant;
        newConversations[conversation.sid].client =
          previousState[conversation.sid].client;
        return newConversations;
      });
    }
  };

  /**
   * @param {import('@twilio/conversations/lib/conversation').Conversation } leftConversation
   * @returns {void}
   */
  const _handleConversationLeft = leftConversation => {
    _setConversationsByConversationSID(previousState => {
      delete previousState[leftConversation.sid];
    });
  };

  const _handleConversationUpdated = updatedConversation => {
    _setConversationsByConversationSID(previousState =>
      Object.values(previousState).reduce((accumulator, conversation) => {
        return {
          ...accumulator,
          [conversation.sid]:
            conversation.sid === updatedConversation.sid
              ? updatedConversation
              : conversation,
        };
      }, {})
    );
  };

  /** @returns {Promise<number>} */
  const _getUnreadConversationsCount = useCallback(async () => {
    /** Prevent useEffect from calling this multiple times and causing race conditions. */
    if (conversationsCountLoading) return;
    _setConversationsCountLoading(true);

    await new Promise(resolve => setTimeout(resolve, 1000));

    /**
     * @param {Promise<number>} promise
     * @param {(import('@twilio/conversations/lib/conversation').Conversation)} conversation
     * @returns {Promise<number>}
     */
    const reducer = async (promise, conversation) => {
      const accumulator = await promise;
      const unreadMessagesCount = conversation?.getUnreadMessagesCount
        ? await conversation.getUnreadMessagesCount()
        : conversation.unreadMessagesCount;

      return {
        count: accumulator.count + (unreadMessagesCount ? 1 : 0),
        newUnreadMessagesCountByConversationSID: {
          ...accumulator.newUnreadMessagesCountByConversationSID,
          [conversation.sid]: unreadMessagesCount ?? 0,
        },
      };
    };

    const { count, newUnreadMessagesCountByConversationSID } =
      await Object.values(conversationsByConversationSID).reduce(
        reducer,
        Promise.resolve({
          count: 0,
          newUnreadMessagesCountByConversationSID: {},
        })
      );

    _setUnreadConversationsCount(count);
    _setUnreadMessagesCountByConversationSID(
      newUnreadMessagesCountByConversationSID
    );
    _setConversationsCountLoading(false);
  }, [conversationsByConversationSID]);

  /**
   * @description
   * Update the conversations state with the Conversation the user wants to
   * archive.
   * @param {string} conversationSid
   * @param {boolean} newState - Whether to archive (true) or unarchive (false) the conversation
   * @returns {Promise<void>}
   */
  const handleUpdateConversationArchivedState = async (
    conversationSid,
    newState
  ) => {
    await graphql(updateConversationAttributes, {
      input: {
        conversationSid,
        attributes: { archived: newState },
      },
    });
  };

  /**
   * @description
   * Mark the specified conversation as "read" (past tense) by updating its
   * lastMessageIndex.
   */
  const markConversationAsRead = useCallback(
    async conversationSID => {
      const conversation = conversationsByConversationSID[conversationSID];
      await conversation.setAllMessagesRead();
      await markConversationReadOnServer(conversationSID);
      // await _getUnreadConversationsCount();
    },
    [conversationsByConversationSID]
  );

  /**
   * @description
   * Ensures there are participants before initializing the conversation from the sdk
   *
   */
  const ensureConversationParticipants = async conversation => {
    await graphql(ensureParticipants, {
      conversationId: conversation.id,
      conversationSid: conversation.sid,
      uniqueName: conversation.uniqueName,
    });
  };

  useEffect(() => {
    if (!conversationsFromServer) return;

    _setConversationsByConversationSID(conversationsFromServer);
  }, [conversationsFromServer]);

  useEffect(() => {
    _getUnreadConversationsCount();
  }, [conversationsByConversationSID]);

  return {
    handleUpdateConversationArchivedState,
    conversations: rawConversations, // Conversation POJOs for reliable state updates
    unreadConversationsCount,
    unreadMessagesCountByConversationSID,
    markConversationAsRead,
    ensureConversationParticipants,

    /**
     * Private members only meant to be used by the TwilioProvider and its
     * composed hooks (not intended to be exposed to consuming components).
     */
    _Conversations: conversationsByConversationSID, // Twilio Conversation instances
    _handleConversationJoined,
    _handleConversationLeft,
    _handleConversationUpdated,
    _setConversationsByConversationSID,
  };
};
